import { useQuery } from '@tanstack/react-query';

import { getRepositoryTags } from '@/react/portainer/registries/repositories/queries/useRepositoryTags';
import { queryKeys } from '@/react/portainer/registries/repositories/queries/queryKeys';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { RegistryId } from '@/react/portainer/registries/types/registry';

const TAG_PAGE_SIZE = 100;
const TAGS_STALE_TIME = 60 * 1000;

/**
 * useAddonImageTags lists the tags published for an image in a Portainer registry,
 * so the tag can be picked rather than typed.
 *
 * A failed lookup resolves to no tags rather than to an error: the image path is
 * typed freely, so every keystroke the field commits looks up a repository that may
 * not exist yet, and each is its own query key — and so would be its own toast.
 * Shares the registry browser's query key, so a listing either view already
 * fetched is reused.
 */
export function useAddonImageTags({
  registryId,
  repository,
  environmentId,
}: {
  registryId?: RegistryId;
  repository: string;
  environmentId?: EnvironmentId;
}) {
  return useQuery({
    queryKey: queryKeys.repositoryTags(registryId ?? 0, repository),
    queryFn: async () => {
      try {
        const { tags } = await getRepositoryTags({
          registryId: registryId ?? 0,
          repository,
          environmentId,
          n: TAG_PAGE_SIZE,
          last: '',
        });

        return sortTagsNewestFirst(tags);
      } catch {
        return [];
      }
    },
    enabled: !!registryId && !!repository && !!environmentId,
    // A half-typed or mistyped path is an expected miss, not a transient fault.
    retry: false,
    staleTime: TAGS_STALE_TIME,
  });
}

/**
 * Newest first, so the head of the list is the most recent — the same order the
 * chart's version list uses. Compared numerically so "1.10.0" sorts above "1.9.0",
 * which a plain string compare gets backwards; non-numeric tags fall back to
 * ordinary collation.
 */
function sortTagsNewestFirst(tags: Array<string>): Array<string> {
  return [...tags].sort((a, b) =>
    b.localeCompare(a, undefined, { numeric: true })
  );
}
